import React, { lazy, type ComponentType } from 'react'
import { DashSwitch } from '@retailer-platform/dashboard/routing'
import { DomainRoute } from '../utils/domain/routingComponents'
import { retailerCollectionsV3AccessControl } from '../access-control/retailerCollectionsV3Access.configuration'
import {
  retailerCollectionsFlyersAccessControl,
  flyerPageBuilderAccessControl,
  retailerCollectionsDigitalFlyersAccessControl,
} from '../access-control/retailerCollectionsFlyersAccess.configuration'
import { omeletAccessControl } from '../access-control/omeletAccess.configuration'

// V3
const RetailerCollectionsV3ListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "RetailerCollectionsV3ListPage" */
      '../pages/RetailerCollectionsV3ListPage'
    )
)
const RetailerCollectionsV3CreatePage = lazy(
  () =>
    import(
      /* webpackChunkName: "RetailerCollectionsV3CreatePage" */
      '../pages/RetailerCollectionsV3CreatePage'
    )
)
const RetailerCollectionsV3ViewPage = lazy(
  () =>
    import(
      /* webpackChunkName: "RetailerCollectionsV3ViewPage" */
      '../pages/RetailerCollectionsV3ViewPage'
    )
)
const RetailerCollectionsV3EditPage = lazy(
  () =>
    import(
      /* webpackChunkName: "RetailerCollectionsV3EditPage" */
      '../pages/RetailerCollectionsV3EditPage'
    )
)

const RetailerCollectionsFlyersPage = lazy(
  () => import('../pages/flyers/RetailerCollectionsFlyersPage')
)
const RetailerCollectionsFlyersCreatePage = lazy(
  () => import('../pages/flyers-create/RetailerCollectionsFlyersCreatePage')
)
const RetailerCollectionsFlyersBuildPage = lazy(
  () => import('../pages/flyers-build/RetailerCollectionsFlyersBuildPage')
)
const RetailerCollectionsFlyersReviewPage = lazy(
  () => import('../pages/flyers-review/RetailerCollectionsFlyersReviewPage')
)
const RetailerCollectionsFlyersValidationPage = lazy(
  () => import('../pages/flyers-validation/RetailerCollectionsFlyersValidationPage')
)
const RetailerCollectionsFlyersOmeletRunViewPage = lazy(
  () => import('../pages/omelet/flyers-omelet-run-view/RetailerCollectionsFlyersOmeletRunViewPage')
)
const RetailerCollectionsFlyersOmeletEditorPage = lazy(
  () => import('../pages/omelet/flyers-omelet-editor/RetailerCollectionsFlyersOmeletEditorPage')
)
const RetailerCollectionsFlyersOmeletPage = lazy(
  () => import('../pages/omelet/flyers-omelet/RetailerCollectionsFlyersOmeletPage')
)
const RetailerCollectionsFlyersPageBuilderPage = lazy(
  () => import('../pages/flyers-page-builder/RetailerCollectionsFlyersPageBuilderPage')
)
const RetailerCollectionsDigitalFlyersReviewPage = lazy(
  () => import('../pages/digital-flyers/RetailerCollectionsDigitalFlyersReviewPage')
)

// @hygen:inject page-import

export const v1Router = (
  <DomainRoute route="retailer-collections" accessControl={retailerCollectionsV3AccessControl}>
    <DashSwitch>
      <DomainRoute
        exact
        route="retailer-collections"
        component={RetailerCollectionsV3ListPage}
        scopePicker={{
          titles: ['retailerCollectionsDomain.v2.list.title'],
        }}
      />
      <DomainRoute
        exact
        route="retailer-collections-list"
        component={RetailerCollectionsV3ListPage}
      />
      <DomainRoute
        exact
        route="retailer-collections-browse"
        component={RetailerCollectionsV3ListPage}
      />
      <DomainRoute
        exact
        route="retailer-collections-create"
        component={RetailerCollectionsV3CreatePage}
      />
      <DomainRoute
        exact
        route="retailer-collections-view"
        component={RetailerCollectionsV3ViewPage}
      />
      <DomainRoute
        exact
        route="retailer-collections-edit"
        component={RetailerCollectionsV3EditPage as ComponentType<any>}
      />

      {/* Flyers */}
      <DomainRoute
        exact
        route="retailer-collections-flyers-create"
        tool="flyers-dashboard-tool"
        component={RetailerCollectionsFlyersCreatePage as ComponentType<any>}
        scopePicker={{
          titles: ['retailerCollectionsDomain.flyers.list.title'],
        }}
      />
      <DomainRoute
        exact
        route="retailer-collections-flyers-page-builder"
        component={RetailerCollectionsFlyersPageBuilderPage as ComponentType<any>}
        accessControl={flyerPageBuilderAccessControl}
      />

      {/* v3 */}
      <DomainRoute
        exact
        route="retailer-collections-v3-view"
        component={RetailerCollectionsV3ViewPage}
      />
      <DomainRoute
        exact
        route="retailer-collections-v3-edit"
        component={RetailerCollectionsV3EditPage as ComponentType<any>}
      />
      {/* @hygen:inject router-route injection point for routes, please do not delete */}
    </DashSwitch>
  </DomainRoute>
)

export const flyerRouter = (
  <DomainRoute
    route="retailer-collections-flyers"
    tool="flyers-dashboard-tool"
    accessControl={retailerCollectionsFlyersAccessControl}
  >
    <DashSwitch>
      <DomainRoute
        exact
        route="retailer-collections-flyers"
        tool="flyers-dashboard-tool"
        component={RetailerCollectionsFlyersPage}
      />
      <DomainRoute
        exact
        route="retailer-collections-flyers-build"
        tool="flyers-dashboard-tool"
        component={RetailerCollectionsFlyersBuildPage as ComponentType<any>}
      />
      <DomainRoute
        exact
        route="retailer-collections-flyers-review"
        tool="flyers-dashboard-tool"
        component={RetailerCollectionsFlyersReviewPage as ComponentType<any>}
      />
      <DomainRoute
        exact
        route="retailer-collections-flyers-validation"
        tool="flyers-dashboard-tool"
        component={RetailerCollectionsFlyersValidationPage as ComponentType<any>}
      />
      {/* Omelet routes */}
      <DomainRoute
        exact
        route="retailer-collections-flyers-omelet-run-view"
        tool="flyers-dashboard-tool"
        accessControl={omeletAccessControl}
        component={RetailerCollectionsFlyersOmeletRunViewPage as ComponentType<any>}
      />
      <DomainRoute
        exact
        route="retailer-collections-flyers-omelet-editor"
        tool="flyers-dashboard-tool"
        accessControl={omeletAccessControl}
        component={RetailerCollectionsFlyersOmeletEditorPage}
      />
      <DomainRoute
        exact
        route="retailer-collections-flyers-omelet"
        tool="flyers-dashboard-tool"
        accessControl={omeletAccessControl}
        component={RetailerCollectionsFlyersOmeletPage}
      />
    </DashSwitch>
  </DomainRoute>
)

export const digitalFlyerRouter = (
  <DomainRoute
    route="retailer-collections-digital-flyers"
    tool="flyers-dashboard-tool"
    accessControl={retailerCollectionsDigitalFlyersAccessControl}
  >
    <DashSwitch>
      <DomainRoute
        exact
        route="retailer-collections-digital-flyers-review"
        tool="flyers-dashboard-tool"
        component={RetailerCollectionsDigitalFlyersReviewPage as ComponentType<any>}
      />
    </DashSwitch>
  </DomainRoute>
)

export const v3Router = (
  <DomainRoute route="retailer-collections-v3" accessControl={retailerCollectionsV3AccessControl}>
    <DashSwitch>
      <DomainRoute
        exact
        route="retailer-collections-v3"
        component={RetailerCollectionsV3ListPage}
      />
      <DomainRoute
        exact
        route="retailer-collections-v3-list"
        component={RetailerCollectionsV3ListPage}
      />
      <DomainRoute
        exact
        route="retailer-collections-v3-create"
        component={RetailerCollectionsV3CreatePage}
      />
      <DomainRoute
        exact
        route="retailer-collections-v3-view-global"
        component={RetailerCollectionsV3ViewPage}
      />

      {/* @hygen:inject router-route injection point for routes, please do not delete */}
    </DashSwitch>
  </DomainRoute>
)
